import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, User, Phone, Lock, Shield, ArrowLeft, CheckCircle, AlertCircle, Building2, UserCheck } from "lucide-react";
import logo from "../assets/images/company-logo.png";
import { useIsMobile } from "../components/ui/use-mobile";

type LoginTab = 'account' | 'phone';

// 错误码到中文的映射
function translateError(error: string | undefined): string {
  if (!error) return "操作失败，请稍后重试";
  
  const errorMap: Record<string, string> = {
    'invalid_credentials': '账号或密码错误',
    'missing_params': '参数不完整，请检查输入',
    'server_error': '服务器错误，请稍后重试',
    'too_frequent': '操作过于频繁，请稍后再试',
    'sms_failed': '短信发送失败，请稍后重试',
    'sms_code_expired': '短信验证码已过期，请重新获取',
    'invalid_sms_code': '短信验证码错误',
    'user_not_found': '用户不存在',
    'invalid_token': '登录已过期，请重新登录',
    'no_token': '未登录，请先登录',
    'duplicate_account': '账号已存在',
    'duplicate_company': '企业名称已存在',
    'duplicate_email': '邮箱已存在',
    'duplicate_phone': '手机号已存在',
    'captcha_expired': '图形验证码已过期，请刷新后重试',
    'account_frozen': '您的账号已被冻结，请联系管理员删除后重新注册！',
    'account_reviewing': '您的账号还在审核中，请您留意短信通知，在审核通过后再尝试登录，感谢您的配合！',
    'account_rejected': '您的信息已被打回，请重新完善',
  };
  
  return errorMap[error] || error;
}

// 获取登录类型配置
const getLoginTypeConfig = (type: string) => {
  switch (type) {
    case 'supplier':
      return { title: '供应商登录', color: '#003c8c', icon: Building2, desc: '企业采购供应商' };
    case 'expert':
      return { title: '评审人员登录', color: '#059669', icon: UserCheck, desc: '' };
    case 'purchaser':
      return { title: '采购人登录', color: '#7c3aed', icon: User, desc: '采购人/招标人' };
    default:
      return { title: '供应商登录', color: '#003c8c', icon: Building2, desc: '企业采购供应商' };
  }
};

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const loginType = searchParams.get('type') || 'supplier';
  const [activeTab, setActiveTab] = useState<LoginTab>(loginType === 'expert' ? 'phone' : loginType === 'purchaser' ? 'account' : 'account');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showReviewingModal, setShowReviewingModal] = useState(false);
  const [showRejectedModal, setShowRejectedModal] = useState(false);
  const isMobile = useIsMobile();

  const typeConfig = getLoginTypeConfig(loginType);
  const TypeIcon = typeConfig.icon;

  // 处理被拒绝的供应商登录
  const handleRejected = (user: any, token: string) => {
    // 保存用户信息和token
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setShowRejectedModal(true);
  };

  // 确认被拒绝弹窗后跳转
  const handleRejectedConfirm = () => {
    setShowRejectedModal(false);
    navigate('/complete-info');
  };

  // 采购人/招标人只有账号登录，专家只有手机登录
  const showPhoneTab = loginType !== 'purchaser';
  const showAccountTab = loginType !== 'purchaser' && loginType !== 'expert';

  if (showForgotPassword) {
    return <ForgotPasswordPage onCancel={() => setShowForgotPassword(false)} typeConfig={typeConfig} />;
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
      {/* 左侧装饰区域 - 60%宽度，渐变背景 - 移动端隐藏 */}
      {!isMobile && (
        <div style={{
          flex: '0 0 60%',
          background: `linear-gradient(135deg, ${typeConfig.color} 0%, ${typeConfig.color}dd 50%, ${typeConfig.color}bb 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          padding: '40px'
        }}>
          {/* 装饰圆形 */}
          <div style={{
            position: 'absolute',
            top: '-20%',
            right: '-10%',
            width: '500px',
            height: '500px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-15%',
            left: '-8%',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)',
          }} />
          <div style={{
            position: 'absolute',
            top: '20%',
            left: '10%',
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.03)',
          }} />
          
          <div style={{ maxWidth: '500px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <div style={{
              width: '120px',
              height: '120px',
              borderRadius: '30px',
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 32px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)'
            }}>
              <TypeIcon style={{ width: '60px', height: '60px', color: 'white' }} />
            </div>
            <h1 style={{ 
              fontSize: '36px', 
              fontWeight: 700, 
              color: 'white',
              marginBottom: loginType === 'supplier' ? '40px' : '16px',
              textShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}>
              {typeConfig.title}
            </h1>
            {loginType !== 'supplier' && (
              <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.85)', marginBottom: '40px' }}>
                {typeConfig.desc}
              </p>
            )}
            
            {/* 特性列表 */}
            <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[
                loginType === 'purchaser' ? '智能监测，预防串通投标' : '安全可靠的身份验证系统',
                '一站式招投标服务平台',
                '7x24小时在线技术支持'
              ].map((item, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CheckCircle style={{ width: '16px', height: '16px', color: 'white' }} />
                  </div>
                  <span style={{ fontSize: '15px', color: 'rgba(255,255,255,0.9)' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* 右侧登录区域 - 移动端全宽，桌面端40%宽度 */}
      <div style={{
        flex: isMobile ? '1 1 100%' : '0 0 40%',
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ width: '100%', maxWidth: '420px', padding: isMobile ? '24px' : '48px' }}>
          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <img
              src={logo}
              alt="德润慧通"
              style={{ height: '80px', margin: '0 auto' }}
            />
          </div>
          
          {/* 标题 */}
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#1f2937', marginBottom: '8px' }}>
              欢迎登录
            </h2>
            <p style={{ fontSize: '14px', color: '#6b7280' }}>{typeConfig.title}</p>
          </div>

          {/* 页签 */}
          {showPhoneTab && showAccountTab && (
            <div style={{ 
              display: 'flex', 
              backgroundColor: '#f3f4f6',
              borderRadius: '12px',
              padding: '4px',
              marginBottom: '28px'
            }}>
              <button
                onClick={() => setActiveTab('account')}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  border: 'none',
                  backgroundColor: activeTab === 'account' ? 'white' : 'transparent',
                  color: activeTab === 'account' ? typeConfig.color : '#6b7280',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: activeTab === 'account' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'
                }}
              >
                账号登录
              </button>
              <button
                onClick={() => setActiveTab('phone')}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  border: 'none',
                  backgroundColor: activeTab === 'phone' ? 'white' : 'transparent',
                  color: activeTab === 'phone' ? typeConfig.color : '#6b7280',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: activeTab === 'phone' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'
                }}
              >
                手机登录
              </button>
            </div>
          )}

          {/* 登录表单 */}
          {activeTab === 'account' ? (
            <AccountLoginForm 
              onForgotPassword={() => setShowForgotPassword(true)} 
              loginType={loginType}
              typeConfig={typeConfig}
              onLoginSuccess={() => navigate('/')}
              onReviewing={() => setShowReviewingModal(true)}
              onRejected={handleRejected}
            />
          ) : (
            <PhoneLoginForm 
              loginType={loginType}
              typeConfig={typeConfig}
              onLoginSuccess={() => navigate('/')}
              onReviewing={() => setShowReviewingModal(true)}
              onRejected={handleRejected}
            />
          )}
        </div>
      </div>
      
      {/* 审核中弹窗 */}
      {showReviewingModal && (
        <>
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
            zIndex: 50
          }} />
          <div style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: '16px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '20px',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              maxWidth: '400px',
              width: '100%',
              padding: '40px',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 24px',
                backgroundColor: '#fef3c7',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AlertCircle style={{ width: '40px', height: '40px', color: '#f59e0b' }} />
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#1f2937', marginBottom: '12px' }}>
                账号审核提示
              </h3>
              <p style={{ color: '#6b7280', marginBottom: '32px', lineHeight: 1.6 }}>
                您的账号还在审核中，请您留意短信通知，在审核通过后再尝试登录，感谢您的配合！
              </p>
              <button
                onClick={() => setShowReviewingModal(false)}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: typeConfig.color,
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer'
                }}
              >
                已知晓
              </button>
            </div>
          </div>
        </>
      )}

      {/* 审批不通过弹窗 */}
      {showRejectedModal && (
        <>
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
            zIndex: 50
          }} />
          <div style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: '16px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '20px',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              maxWidth: '400px',
              width: '100%',
              padding: '40px',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 24px',
                backgroundColor: '#fef2f2',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AlertCircle style={{ width: '40px', height: '40px', color: '#ef4444' }} />
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#1f2937', marginBottom: '12px' }}>
                审核未通过
              </h3>
              <p style={{ color: '#6b7280', marginBottom: '32px', lineHeight: 1.6 }}>
                您的信息已被打回，请重新完善
              </p>
              <button
                onClick={handleRejectedConfirm}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: typeConfig.color,
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer'
                }}
              >
                确定
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

type AccountLoginState = {
  account: string;
  password: string;
  captchaId: string;
  captchaCode: string;
};

function AccountLoginForm({ onForgotPassword, loginType = 'supplier', typeConfig, onLoginSuccess, onReviewing, onRejected }: { 
  onForgotPassword: () => void; 
  loginType?: string; 
  typeConfig: any;
  onLoginSuccess?: (user: any) => void; 
  onReviewing?: () => void;
  onRejected?: (user: any, token: string) => void;
}) {
  const [fs, setFs] = useState<AccountLoginState>({
    account: "",
    password: "",
    captchaId: "",
    captchaCode: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pwVisible, setPwVisible] = useState(false);
  const [captchaSvg, setCaptchaSvg] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => { refreshCaptcha(); }, []);

  function refreshCaptcha() {
    fetch('/api/captcha')
      .then(r => r.json())
      .then((d: any) => {
        if (d && d.id && d.svg) {
          setFs((s: AccountLoginState) => ({ ...s, captchaId: d.id, captchaCode: "" }));
          setCaptchaSvg(d.svg);
        }
      })
      .catch(err => console.error('获取验证码错误:', err));
  }

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!fs.account.trim()) e.account = loginType === 'supplier' ? "请输入统一社会信用代码" : "请输入账号";
    if (!fs.password) e.password = "请输入密码";
    if (!fs.captchaCode.trim()) e.captchaCode = "请输入图形验证码";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleLogin() {
    if (!validate()) return;
    
    setLoading(true);
    try {
      const captchaVerify = await fetch('/api/captcha/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: fs.captchaId, code: fs.captchaCode })
      }).then(r => r.json()).catch(() => ({ ok: false }));

      if (!captchaVerify || !captchaVerify.ok) {
        setErrors({ captchaCode: "图形验证码错误" });
        refreshCaptcha();
        setLoading(false);
        return;
      }

      const resp = await fetch('/api/login/account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account: fs.account,
          password: fs.password,
          login_type: loginType,
        })
      }).then(r => r.json()).catch(() => ({ ok: false }));

      if (resp && resp.ok && resp.token && resp.user) {
        try {
          localStorage.setItem('token', resp.token);
          localStorage.setItem('user', JSON.stringify(resp.user));
          
          const savedToken = localStorage.getItem('token');
          const savedUserStr = localStorage.getItem('user');
          const savedUser = savedUserStr ? JSON.parse(savedUserStr) : null;
          
          if (!savedToken || !savedUser) {
            setErrors({ account: "登录失败：保存用户信息失败" });
            setLoading(false);
            return;
          }
          
          window.dispatchEvent(new CustomEvent('userLogin', { 
            detail: { user: savedUser, token: savedToken } 
          }));
          
          if (savedUser.type === '预注册' && savedUser.create_time) {
            if (onLoginSuccess) onLoginSuccess(savedUser);
          } else {
            navigate('/');
          }
        } catch (e) {
          setErrors({ account: "登录失败：保存用户信息时出错" });
          setLoading(false);
          return;
        }
      } else {
        if (resp?.error === 'account_reviewing') {
          if (onReviewing) onReviewing();
        } else if (resp?.error === 'account_rejected' && resp?.user && resp?.token) {
          if (onRejected) onRejected(resp.user, resp.token);
        } else {
          setErrors({ account: translateError(resp?.error) });
        }
        refreshCaptcha();
      }
    } catch (err) {
      setErrors({ account: "登录失败，请稍后重试" });
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  }

  const inputStyle = (field: string, hasError: boolean) => ({
    width: '100%',
    height: '48px',
    padding: '0 16px 0 48px',
    border: `2px solid ${hasError ? '#ef4444' : focusedField === field ? typeConfig.color : '#e5e7eb'}`,
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
    transition: 'all 0.2s',
    backgroundColor: 'white'
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* 账号 */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>账号</label>
          {errors.account && <span style={{ fontSize: '12px', color: '#ef4444' }}>{errors.account}</span>}
        </div>
        <div style={{ position: 'relative' }}>
          <User style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'account' ? typeConfig.color : '#9ca3af' }} />
          <input
            type="text"
            value={fs.account}
            onChange={(e) => {
              setFs((s) => ({ ...s, account: e.target.value }));
              if (errors.account) setErrors({ ...errors, account: "" });
            }}
            onFocus={() => setFocusedField('account')}
            onBlur={() => setFocusedField(null)}
            style={inputStyle('account', !!errors.account)}
            placeholder={loginType === 'supplier' ? "请输入统一社会信用代码" : "请输入账号"}
          />
        </div>
      </div>

      {/* 密码 */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>密码</label>
          {errors.password && <span style={{ fontSize: '12px', color: '#ef4444' }}>{errors.password}</span>}
        </div>
        <div style={{ position: 'relative' }}>
          <Lock style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'password' ? typeConfig.color : '#9ca3af' }} />
          <input
            type={pwVisible ? 'text' : 'password'}
            value={fs.password}
            onChange={(e) => {
              setFs((s) => ({ ...s, password: e.target.value }));
              if (errors.password) setErrors({ ...errors, password: "" });
            }}
            onFocus={() => setFocusedField('password')}
            onBlur={() => setFocusedField(null)}
            style={{ ...inputStyle('password', !!errors.password), paddingRight: '48px' }}
            placeholder="请输入密码"
          />
          <button
            type="button"
            onClick={() => setPwVisible(!pwVisible)}
            style={{
              position: 'absolute',
              right: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0
            }}
          >
            {pwVisible ? <Eye style={{ width: '20px', height: '20px', color: '#9ca3af' }} /> : <EyeOff style={{ width: '20px', height: '20px', color: '#9ca3af' }} />}
          </button>
        </div>
      </div>

      {/* 图形验证码 */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>图形验证码</label>
          {errors.captchaCode && <span style={{ fontSize: '12px', color: '#ef4444' }}>{errors.captchaCode}</span>}
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Shield style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'captcha' ? typeConfig.color : '#9ca3af' }} />
            <input
              type="text"
              value={fs.captchaCode}
              onChange={(e) => {
                setFs((s) => ({ ...s, captchaCode: e.target.value }));
                if (errors.captchaCode) setErrors({ ...errors, captchaCode: "" });
              }}
              onFocus={() => setFocusedField('captcha')}
              onBlur={() => setFocusedField(null)}
              style={inputStyle('captcha', !!errors.captchaCode)}
              placeholder="请输入验证码"
            />
          </div>
          <div
            onClick={refreshCaptcha}
            style={{
              width: '120px',
              height: '48px',
              backgroundColor: '#f3f4f6',
              borderRadius: '12px',
              overflow: 'hidden',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #e5e7eb'
            }}
          >
            {captchaSvg ? (
              <div dangerouslySetInnerHTML={{ __html: captchaSvg }} />
            ) : (
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>点击刷新</span>
            )}
          </div>
        </div>
      </div>

      {/* 登录按钮 */}
      <button
        onClick={handleLogin}
        disabled={loading}
        style={{
          width: '100%',
          height: '52px',
          backgroundColor: loading ? '#9ca3af' : typeConfig.color,
          color: 'white',
          fontSize: '16px',
          fontWeight: 600,
          border: 'none',
          borderRadius: '12px',
          cursor: loading ? 'not-allowed' : 'pointer',
          marginTop: '8px',
          transition: 'all 0.2s',
          boxShadow: loading ? 'none' : `0 4px 15px ${typeConfig.color}40`
        }}
      >
        {loading ? '登录中...' : '登录'}
      </button>

      {/* 忘记密码或注册 */}
      <div style={{ textAlign: 'center' }}>
        {loginType === 'supplier' ? (
          <>
            <p style={{ fontSize: '13px', color: '#6b7280' }}>
              还没有账号？
              <span
                onClick={() => navigate('/register')}
                style={{
                  color: '#2563eb',
                  cursor: 'pointer',
                  marginLeft: '4px',
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = 'none';
                }}
              >
                立即注册
              </span>
            </p>
            <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px' }}>
              <span
                onClick={() => navigate('/downloads')}
                style={{
                  color: '#2563eb',
                  cursor: 'pointer',
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = 'none';
                }}
              >
                忘记密码？
              </span>
            </p>
          </>
        ) : (
          <p style={{ fontSize: '13px', color: '#9ca3af' }}>若忘记密码请联系管理员重置</p>
        )}
      </div>
    </div>
  );
}

type PhoneLoginState = {
  phone: string;
  captchaId: string;
  captchaCode: string;
  smsCode: string;
};

function PhoneLoginForm({ loginType = 'supplier', typeConfig, onLoginSuccess, onReviewing, onRejected }: { 
  loginType?: string; 
  typeConfig: any;
  onLoginSuccess?: (user: any) => void; 
  onReviewing?: () => void;
  onRejected?: (user: any, token: string) => void;
}) {
  const [fs, setFs] = useState<PhoneLoginState>({
    phone: "",
    captchaId: "",
    captchaCode: "",
    smsCode: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [captchaSvg, setCaptchaSvg] = useState<string>("");
  const [smsSending, setSmsSending] = useState(false);
  const [smsLeft, setSmsLeft] = useState(0);
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => { refreshCaptcha(); }, []);

  function refreshCaptcha() {
    fetch('/api/captcha')
      .then(r => r.json())
      .then(d => {
        if (d && d.id && d.svg) {
          setFs((s: PhoneLoginState) => ({ ...s, captchaId: d.id, captchaCode: "" }));
          setCaptchaSvg(d.svg);
        }
      })
      .catch(err => console.error('获取验证码错误:', err));
  }

  async function getSmsCode() {
    if (smsLeft > 0 || smsSending) return;
    if (!/^\d{11}$/.test(fs.phone)) {
      setErrors(prev => ({ ...prev, phone: "手机号需为11位数字" }));
      return;
    }
    if (!fs.captchaCode) {
      setErrors(prev => ({ ...prev, captchaCode: "请输入图形验证码" }));
      return;
    }

    const captchaVerify = await fetch('/api/captcha/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: fs.captchaId, code: fs.captchaCode })
    }).then(r => r.json()).catch(() => ({ ok: false }));

    if (!captchaVerify?.ok) {
      setErrors(prev => ({ ...prev, captchaCode: "图形验证码错误" }));
      refreshCaptcha();
      return;
    }

    setSmsSending(true);
    const r = await fetch('/api/sms/get', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone: fs.phone, template_code: 'SMS_225770289' })
    }).then(r => r.json()).catch(() => ({ ok: false }));
    setSmsSending(false);
    
    if (r?.ok) {
      setSmsLeft(60);
    } else {
      setErrors(prev => ({ ...prev, smsCode: translateError(r?.error) }));
    }
  }

  useEffect(() => {
    if (smsLeft <= 0) return;
    const t = setInterval(() => setSmsLeft(s => s > 0 ? s - 1 : 0), 1000);
    return () => clearInterval(t);
  }, [smsLeft]);

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!/^\d{11}$/.test(fs.phone)) e.phone = "手机号需为11位数字";
    if (!fs.captchaCode.trim()) e.captchaCode = "请输入图形验证码";
    if (!fs.smsCode.trim()) e.smsCode = "请输入短信验证码";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleLogin() {
    if (!validate()) return;

    setLoading(true);
    try {
      const resp = await fetch('/api/login/phone', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: fs.phone,
          sms_code: fs.smsCode,
          login_type: loginType,
        })
      }).then(r => r.json()).catch(() => ({ ok: false }));

      if (resp?.ok && resp.token && resp.user) {
        try {
          localStorage.setItem('token', resp.token);
          localStorage.setItem('user', JSON.stringify(resp.user));
          
          const savedUser = JSON.parse(localStorage.getItem('user') || '{}');
          
          window.dispatchEvent(new CustomEvent('userLogin', { 
            detail: { user: savedUser, token: resp.token } 
          }));
          
          if (savedUser.type === '预注册' && savedUser.create_time) {
            if (onLoginSuccess) onLoginSuccess(savedUser);
          } else {
            navigate('/');
          }
        } catch (e) {
          setErrors({ smsCode: "登录失败：保存用户信息时出错" });
        }
      } else {
        if (resp?.error === 'account_reviewing') {
          if (onReviewing) onReviewing();
        } else if (resp?.error === 'account_rejected' && resp?.user && resp?.token) {
          if (onRejected) onRejected(resp.user, resp.token);
        } else {
          setErrors({ smsCode: translateError(resp?.error) });
        }
        refreshCaptcha();
      }
    } catch (err) {
      setErrors({ smsCode: "登录失败，请稍后重试" });
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  }

  const inputStyle = (field: string, hasError: boolean) => ({
    width: '100%',
    height: '48px',
    padding: '0 16px 0 48px',
    border: `2px solid ${hasError ? '#ef4444' : focusedField === field ? typeConfig.color : '#e5e7eb'}`,
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
    transition: 'all 0.2s',
    backgroundColor: 'white'
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* 手机号 */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>手机号</label>
          {errors.phone && <span style={{ fontSize: '12px', color: '#ef4444' }}>{errors.phone}</span>}
        </div>
        <div style={{ position: 'relative' }}>
          <Phone style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'phone' ? typeConfig.color : '#9ca3af' }} />
          <input
            type="text"
            value={fs.phone}
            onChange={(e) => {
              setFs((s) => ({ ...s, phone: e.target.value }));
              if (errors.phone) setErrors({ ...errors, phone: "" });
            }}
            onFocus={() => setFocusedField('phone')}
            onBlur={() => setFocusedField(null)}
            style={inputStyle('phone', !!errors.phone)}
            placeholder="请输入手机号"
          />
        </div>
      </div>

      {/* 图形验证码 */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>图形验证码</label>
          {errors.captchaCode && <span style={{ fontSize: '12px', color: '#ef4444' }}>{errors.captchaCode}</span>}
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Shield style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'captcha' ? typeConfig.color : '#9ca3af' }} />
            <input
              type="text"
              value={fs.captchaCode}
              onChange={(e) => {
                setFs((s) => ({ ...s, captchaCode: e.target.value }));
                if (errors.captchaCode) setErrors({ ...errors, captchaCode: "" });
              }}
              onFocus={() => setFocusedField('captcha')}
              onBlur={() => setFocusedField(null)}
              style={inputStyle('captcha', !!errors.captchaCode)}
              placeholder="请输入验证码"
            />
          </div>
          <div
            onClick={refreshCaptcha}
            style={{
              width: '120px',
              height: '48px',
              backgroundColor: '#f3f4f6',
              borderRadius: '12px',
              overflow: 'hidden',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #e5e7eb'
            }}
          >
            {captchaSvg ? (
              <div dangerouslySetInnerHTML={{ __html: captchaSvg }} />
            ) : (
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>点击刷新</span>
            )}
          </div>
        </div>
      </div>

      {/* 短信验证码 */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>短信验证码</label>
          {errors.smsCode && <span style={{ fontSize: '12px', color: '#ef4444' }}>{errors.smsCode}</span>}
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Lock style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'sms' ? typeConfig.color : '#9ca3af' }} />
            <input
              type="text"
              value={fs.smsCode}
              onChange={(e) => {
                setFs((s) => ({ ...s, smsCode: e.target.value }));
                if (errors.smsCode) setErrors({ ...errors, smsCode: "" });
              }}
              onFocus={() => setFocusedField('sms')}
              onBlur={() => setFocusedField(null)}
              style={inputStyle('sms', !!errors.smsCode)}
              placeholder="请输入短信验证码"
            />
          </div>
          <button
            onClick={getSmsCode}
            disabled={!/^\d{11}$/.test(fs.phone) || smsLeft > 0 || smsSending || !fs.captchaCode}
            style={{
              padding: '0 20px',
              height: '48px',
              backgroundColor: (!/^\d{11}$/.test(fs.phone) || smsLeft > 0 || smsSending || !fs.captchaCode) ? '#e5e7eb' : typeConfig.color,
              color: (!/^\d{11}$/.test(fs.phone) || smsLeft > 0 || smsSending || !fs.captchaCode) ? '#9ca3af' : 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: (!/^\d{11}$/.test(fs.phone) || smsLeft > 0 || smsSending || !fs.captchaCode) ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            {smsLeft > 0 ? `${smsLeft}s` : smsSending ? '发送中' : '获取验证码'}
          </button>
        </div>
      </div>

      {/* 登录按钮 */}
      <button
        onClick={handleLogin}
        disabled={loading}
        style={{
          width: '100%',
          height: '52px',
          backgroundColor: loading ? '#9ca3af' : typeConfig.color,
          color: 'white',
          fontSize: '16px',
          fontWeight: 600,
          border: 'none',
          borderRadius: '12px',
          cursor: loading ? 'not-allowed' : 'pointer',
          marginTop: '8px',
          transition: 'all 0.2s',
          boxShadow: loading ? 'none' : `0 4px 15px ${typeConfig.color}40`
        }}
      >
        {loading ? '登录中...' : '登录'}
      </button>
    </div>
  );
}

type ForgotPasswordState = {
  phone: string;
  captchaId: string;
  captchaCode: string;
  smsCode: string;
  password: string;
  confirmPassword: string;
};

function ForgotPasswordPage({ onCancel, typeConfig }: { onCancel: () => void; typeConfig: any }) {
  const [fs, setFs] = useState<ForgotPasswordState>({
    phone: "",
    captchaId: "",
    captchaCode: "",
    smsCode: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [captchaSvg, setCaptchaSvg] = useState<string>("");
  const [smsSending, setSmsSending] = useState(false);
  const [smsLeft, setSmsLeft] = useState(0);
  const [pwVisible, setPwVisible] = useState(false);
  const [cpVisible, setCpVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  useEffect(() => { refreshCaptcha(); }, []);

  function refreshCaptcha() {
    fetch('/api/captcha')
      .then(r => r.json())
      .then((d: any) => {
        if (d && d.id && d.svg) {
          setFs((s: ForgotPasswordState) => ({ ...s, captchaId: d.id, captchaCode: "" }));
          setCaptchaSvg(d.svg);
        }
      })
      .catch(err => console.error('获取验证码错误:', err));
  }

  async function getSmsCode() {
    if (smsLeft > 0 || smsSending) return;
    if (!/^\d{11}$/.test(fs.phone)) {
      setErrors(prev => ({ ...prev, phone: "手机号需为11位数字" }));
      return;
    }
    if (!fs.captchaCode) {
      setErrors(prev => ({ ...prev, captchaCode: "请输入图形验证码" }));
      return;
    }

    const captchaVerify = await fetch('/api/captcha/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: fs.captchaId, code: fs.captchaCode })
    }).then(r => r.json()).catch(() => ({ ok: false }));

    if (!captchaVerify?.ok) {
      setErrors(prev => ({ ...prev, captchaCode: "图形验证码错误" }));
      refreshCaptcha();
      return;
    }

    setSmsSending(true);
    const r = await fetch('/api/sms/get', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone: fs.phone, template_code: 'SMS_227245900' })
    }).then(r => r.json()).catch(() => ({ ok: false }));
    setSmsSending(false);
    
    if (r?.ok) {
      setSmsLeft(60);
    } else {
      setErrors(prev => ({ ...prev, smsCode: translateError(r?.error) }));
    }
  }

  useEffect(() => {
    if (smsLeft <= 0) return;
    const t = setInterval(() => setSmsLeft(s => s > 0 ? s - 1 : 0), 1000);
    return () => clearInterval(t);
  }, [smsLeft]);

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!/^\d{11}$/.test(fs.phone)) e.phone = "手机号需为11位数字";
    if (!fs.captchaCode.trim()) e.captchaCode = "请输入图形验证码";
    if (!fs.smsCode.trim()) e.smsCode = "请输入短信验证码";
    if (!/^[A-Za-z0-9\W_]{8,20}$/.test(fs.password)) e.password = "密码需为8-20位";
    if (fs.password !== fs.confirmPassword) e.confirmPassword = "两次密码不一致";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit() {
    if (!validate()) return;

    setLoading(true);
    try {
      const resp = await fetch('/api/password/reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: fs.phone,
          sms_code: fs.smsCode,
          password: fs.password,
        })
      }).then(r => r.json()).catch(() => ({ ok: false }));

      if (resp?.ok) {
        alert('密码修改成功，请使用新密码登录');
        onCancel();
      } else {
        setErrors({ smsCode: translateError(resp?.error) });
        refreshCaptcha();
      }
    } catch (err) {
      setErrors({ smsCode: "修改失败，请稍后重试" });
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  }

  const inputStyle = (field: string, hasError: boolean) => ({
    width: '100%',
    height: '48px',
    padding: '0 16px 0 48px',
    border: `2px solid ${hasError ? '#ef4444' : focusedField === field ? typeConfig.color : '#e5e7eb'}`,
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
    transition: 'all 0.2s',
    backgroundColor: 'white'
  });

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px', position: 'relative', overflow: 'hidden' }}>
      {/* 背景 */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: `linear-gradient(135deg, ${typeConfig.color}15 0%, ${typeConfig.color}08 50%, #f8fafc 100%)`,
      }} />
      
      {/* 卡片 */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '24px',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        width: '100%',
        maxWidth: '480px',
        padding: '40px',
        position: 'relative',
        zIndex: 1
      }}>
        {/* 返回按钮 */}
        <button
          onClick={onCancel}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#6b7280',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            marginBottom: '24px',
            fontSize: '14px'
          }}
        >
          <ArrowLeft style={{ width: '18px', height: '18px' }} />
          返回登录
        </button>
        
        <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#1f2937', marginBottom: '8px', textAlign: 'center' }}>
          修改密码
        </h2>
        <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '32px', textAlign: 'center' }}>
          请输入您绑定的手机号验证身份
        </p>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* 手机号 */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>手机号</label>
              {errors.phone && <span style={{ fontSize: '12px', color: '#ef4444' }}>{errors.phone}</span>}
            </div>
            <div style={{ position: 'relative' }}>
              <Phone style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'phone' ? typeConfig.color : '#9ca3af' }} />
              <input
                type="text"
                value={fs.phone}
                onChange={(e) => setFs((s) => ({ ...s, phone: e.target.value }))}
                onFocus={() => setFocusedField('phone')}
                onBlur={() => setFocusedField(null)}
                style={inputStyle('phone', !!errors.phone)}
                placeholder="请输入手机号"
              />
            </div>
          </div>

          {/* 图形验证码 */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>图形验证码</label>
              {errors.captchaCode && <span style={{ fontSize: '12px', color: '#ef4444' }}>{errors.captchaCode}</span>}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Shield style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'captcha' ? typeConfig.color : '#9ca3af' }} />
                <input
                  type="text"
                  value={fs.captchaCode}
                  onChange={(e) => setFs((s) => ({ ...s, captchaCode: e.target.value }))}
                  onFocus={() => setFocusedField('captcha')}
                  onBlur={() => setFocusedField(null)}
                  style={inputStyle('captcha', !!errors.captchaCode)}
                  placeholder="请输入验证码"
                />
              </div>
              <div
                onClick={refreshCaptcha}
                style={{
                  width: '120px',
                  height: '48px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #e5e7eb'
                }}
              >
                {captchaSvg ? <div dangerouslySetInnerHTML={{ __html: captchaSvg }} /> : <span style={{ fontSize: '12px', color: '#9ca3af' }}>点击刷新</span>}
              </div>
            </div>
          </div>

          {/* 短信验证码 */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>短信验证码</label>
              {errors.smsCode && <span style={{ fontSize: '12px', color: '#ef4444' }}>{errors.smsCode}</span>}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Lock style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'sms' ? typeConfig.color : '#9ca3af' }} />
                <input
                  type="text"
                  value={fs.smsCode}
                  onChange={(e) => setFs((s) => ({ ...s, smsCode: e.target.value }))}
                  onFocus={() => setFocusedField('sms')}
                  onBlur={() => setFocusedField(null)}
                  style={inputStyle('sms', !!errors.smsCode)}
                  placeholder="请输入短信验证码"
                />
              </div>
              <button
                onClick={getSmsCode}
                disabled={!/^\d{11}$/.test(fs.phone) || smsLeft > 0 || smsSending || !fs.captchaCode}
                style={{
                  padding: '0 20px',
                  height: '48px',
                  backgroundColor: (!/^\d{11}$/.test(fs.phone) || smsLeft > 0 || smsSending || !fs.captchaCode) ? '#e5e7eb' : typeConfig.color,
                  color: (!/^\d{11}$/.test(fs.phone) || smsLeft > 0 || smsSending || !fs.captchaCode) ? '#9ca3af' : 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: (!/^\d{11}$/.test(fs.phone) || smsLeft > 0 || smsSending || !fs.captchaCode) ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                {smsLeft > 0 ? `${smsLeft}s` : smsSending ? '发送中' : '获取验证码'}
              </button>
            </div>
          </div>

          {/* 新密码 */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>新密码</label>
              {errors.password && <span style={{ fontSize: '12px', color: '#ef4444' }}>{errors.password}</span>}
            </div>
            <div style={{ position: 'relative' }}>
              <Lock style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'password' ? typeConfig.color : '#9ca3af' }} />
              <input
                type={pwVisible ? 'text' : 'password'}
                value={fs.password}
                onChange={(e) => setFs((s) => ({ ...s, password: e.target.value }))}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField(null)}
                style={{ ...inputStyle('password', !!errors.password), paddingRight: '48px' }}
                placeholder="8-20位数字、字母或特殊字符"
              />
              <button type="button" onClick={() => setPwVisible(!pwVisible)} style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                {pwVisible ? <Eye style={{ width: '20px', height: '20px', color: '#9ca3af' }} /> : <EyeOff style={{ width: '20px', height: '20px', color: '#9ca3af' }} />}
              </button>
            </div>
          </div>

          {/* 确认密码 */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>确认密码</label>
              {errors.confirmPassword && <span style={{ fontSize: '12px', color: '#ef4444' }}>{errors.confirmPassword}</span>}
            </div>
            <div style={{ position: 'relative' }}>
              <Lock style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'confirm' ? typeConfig.color : '#9ca3af' }} />
              <input
                type={cpVisible ? 'text' : 'password'}
                value={fs.confirmPassword}
                onChange={(e) => setFs((s) => ({ ...s, confirmPassword: e.target.value }))}
                onFocus={() => setFocusedField('confirm')}
                onBlur={() => setFocusedField(null)}
                style={{ ...inputStyle('confirm', !!errors.confirmPassword), paddingRight: '48px' }}
                placeholder="请再次输入密码"
              />
              <button type="button" onClick={() => setCpVisible(!cpVisible)} style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                {cpVisible ? <Eye style={{ width: '20px', height: '20px', color: '#9ca3af' }} /> : <EyeOff style={{ width: '20px', height: '20px', color: '#9ca3af' }} />}
              </button>
            </div>
          </div>

          {/* 按钮 */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                flex: 1,
                height: '52px',
                backgroundColor: loading ? '#9ca3af' : typeConfig.color,
                color: 'white',
                fontSize: '16px',
                fontWeight: 600,
                border: 'none',
                borderRadius: '12px',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? '提交中...' : '提交修改'}
            </button>
            <button
              onClick={onCancel}
              style={{
                flex: 1,
                height: '52px',
                backgroundColor: 'white',
                color: typeConfig.color,
                fontSize: '16px',
                fontWeight: 600,
                border: `2px solid ${typeConfig.color}`,
                borderRadius: '12px',
                cursor: 'pointer'
              }}
            >
              取消
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
